import {EBreakpoint, RESIZE_EVENTS} from '@/common/scripts/constants';
import type {IHeaderEstateData, IHeaderEstateTab} from './types';
import {initSlider, initTabs} from '@/common/scripts/common';
import estateContentTemplate from './templates/header-estate-content.twig';
import estateTabTemplate from './templates/header-estate-tabs.twig';
import Observer from '@/common/scripts/observer';
import {Utils} from '@/common/scripts/utils';

const CLASS_ACTIVE = 'is-active';

const observer = new Observer();

export default class HeaderEstate {
    private wrapper: HTMLElement;
    private openButton: HTMLElement;
    private overlay: HTMLElement | null;
    private tabsWrapper: HTMLElement | null;
    private tabsContent: HTMLElement | null;
    private contentData: IHeaderEstateData;
    private menuState: boolean = false;
    private isMobile: boolean = false;

    constructor(wrapper: HTMLElement, openButton: HTMLElement) {
        this.wrapper = wrapper;
        this.openButton = openButton;
        this.overlay = this.wrapper.querySelector<HTMLElement>('.j-header-estate-overlay');
        this.tabsWrapper = this.wrapper.querySelector<HTMLElement>('.j-tabs');
        this.tabsContent = this.wrapper.querySelector<HTMLElement>('.j-tabs-content');

        if (this.wrapper.dataset['json']) {
            this.contentData = JSON.parse(atob(this.wrapper.dataset['json']));
        }
    }

    init(): void {
        if (!this.tabsWrapper || !this.tabsContent || !this.contentData) {
            console.error('Missing one of required parameters');

            return;
        }

        if (Utils.getWindowWidth() >= EBreakpoint.DESKTOP) {
            this.#initContent();
        } else {
            this.#bindEvents();
        }

        this.#subscribes();
    }

    #bindEvents(): void {
        RESIZE_EVENTS.forEach((event: string) => {
            window.addEventListener(event, Utils.debounce(() => {
                this.isMobile = Utils.getWindowWidth() < EBreakpoint.DESKTOP;

                if (Utils.getWindowWidth() >= EBreakpoint.DESKTOP) {
                    this.#initContent();
                }
            }, 100));
        });
    }

    #initContent(): void {
        this.openButton.addEventListener('mouseenter', () => {
            if (this.isMobile || this.menuState) {
                return;
            }

            this.#toggleContent();
        });

        this.openButton.addEventListener('click', () => {
            if (this.isMobile || (!this.isMobile && !this.menuState)) {
                return;
            }

            this.#toggleContent();
        });

        this.overlay?.addEventListener('mouseenter', () => {
            this.#toggleContent();
        });

        this.#createTabs();
        this.#createContent();
        this.#initTabs();
        // this.#initProjectsSliders();
    }

    #subscribes(): void {
        observer.subscribe('main-menu:open', () => {
            if (!this.menuState) {
                return;
            }

            this.#closeEstateMenu();

            this.menuState = false;
        });
    }

    #createTabs(): void {
        if (!this.tabsWrapper || this.tabsWrapper?.dataset['initData'] || !this.contentData) {
            return;
        }

        Utils.clearHtml(this.tabsWrapper);

        this.contentData.tabs.forEach((tab: IHeaderEstateTab, index: number) => {
            // Подставляем индекс - для определения активного таба при ините
            tab['index'] = index;

            const tabTemplate = estateTabTemplate(tab);

            if (this.tabsWrapper) {
                Utils.insetContent(this.tabsWrapper, tabTemplate);
            }
        });

        this.tabsWrapper.dataset['initData'] = 'true';
    }

    #createContent(): void {
        if (!this.tabsContent || this.tabsContent?.dataset['initData'] || !this.contentData) {
            return;
        }

        Utils.clearHtml(this.tabsContent);

        let i = 0;

        for (const key in this.contentData.tabsContent) {
            if (Object.prototype.hasOwnProperty.call(this.contentData.tabsContent, key)) {
                const content = this.contentData.tabsContent[key];

                // Подставляем индекс - для определения активного таба при ините
                if (content) {
                    content['index'] = i;
                }

                const tabContentTemplate = estateContentTemplate(content);

                if (this.tabsContent) {
                    Utils.insetContent(this.tabsContent, tabContentTemplate);
                }

                i++;
            }
        }

        this.tabsContent.dataset['initData'] = 'true';
    }

    #initTabs(): void {
        const tabs = this.wrapper.querySelector<HTMLElement>('.j-estate-tabs-wrap');

        if (tabs) {
            initTabs(tabs);
        }
    }

    #initProjectsSliders(): void {
        if (!this.tabsContent) {
            return;
        }

        const sliders = [...this.tabsContent.querySelectorAll<HTMLElement>('.j-header-estate-projects')];

        sliders.forEach((slider: HTMLElement) => {
            if (slider.dataset['init'] === 'true') {
                return;
            }

            const sliderTab = slider.closest<HTMLElement>('.j-tabs-content__item');
            const nextEl = sliderTab?.querySelector<HTMLElement>('.j-header-estate-arrow-next');
            const prevEl = sliderTab?.querySelector<HTMLElement>('.j-header-estate-arrow-prev');
            const externalCounter = sliderTab?.querySelector<HTMLElement>('.j-header-estate-projects-counter');

            const sliderOptions = {
                loop          : false,
                slidesPerView : 4,
                slidesPerGroup: 4,
                spaceBetween  : 16,
                speed         : 800,
                // кастомный флаг для установки дата атрибута после инита слайдера
                initFlag      : true,
                navigation    : {
                    nextEl,
                    prevEl
                },
                externalCounter
            };

            initSlider(slider, {sliderOptions});
        });
    }

    #toggleContent(): void {
        if (this.isMobile) {
            return;
        }

        this.menuState ? this.#closeEstateMenu() : this.#openEstateMenu();

        this.menuState ?
            Utils.bodyStatic() :
            Utils.bodyFixed(this.wrapper);

        this.menuState = !this.menuState;
    }

    #openEstateMenu(): void {
        this.openButton.classList.add(CLASS_ACTIVE);
        this.wrapper.classList.add(CLASS_ACTIVE);

        observer.publish('estate-menu:open');
    }

    #closeEstateMenu(): void {
        this.openButton.classList.remove(CLASS_ACTIVE);
        this.wrapper.classList.remove(CLASS_ACTIVE);
    }
}
